import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Plus, CheckCircle2, Clock, AlertCircle, 
  ListChecks, Trash2, Edit2, Share2, User, Calendar,
  ChevronDown, ChevronRight, CheckSquare, Square, Camera,
  MoreVertical, Filter, RefreshCw, MapPin, Tag, Send,
  Timer, Eye, FileText, ClipboardList, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-500 text-white', icon: AlertCircle },
  high: { label: 'High', color: 'bg-orange-500 text-white', icon: AlertCircle },
  normal: { label: 'Normal', color: 'bg-blue-500 text-white', icon: Clock },
  low: { label: 'Low', color: 'bg-gray-400 text-white', icon: Clock },
};

const CATEGORY_OPTIONS = [
  'Electrical', 'Plumbing', 'HVAC', 'Finish', 'Carpentry', 
  'Drywall', 'Paint', 'Flooring', 'Cabinetry', 'Appliances', 
  'Doors & Windows', 'Roofing', 'Exterior', 'Landscaping', 'Other'
];

const PHASE_OPTIONS = [
  'Pre-Drywall', 'Pre-Paint', 'Pre-Flooring', 'Pre-Cabinet', 
  'Pre-Countertop', 'Final', 'Post-Occupancy', 'Warranty'
];

export default function QualityControlPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const darkMode = document.documentElement.classList.contains('dark');
  
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [punchLists, setPunchLists] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [expandedLists, setExpandedLists] = useState({});
  
  // Dialogs
  const [showNewListDialog, setShowNewListDialog] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // New list form
  const [newList, setNewList] = useState({
    title: '',
    description: '',
    phase: ''
  });
  
  // New item form
  const [newItem, setNewItem] = useState({
    description: '',
    location: '',
    category: '',
    priority: 'normal',
    assigned_to: '',
    notes: ''
  });
  
  // Share form
  const [shareContractorId, setShareContractorId] = useState('');
  
  useEffect(() => {
    fetchData();
  }, [projectId]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectRes, listsRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/punch-lists`),
        axios.get(`${API}/contractors`).catch(() => ({ data: [] }))
      ]);
      
      setProject(projectRes.data);
      setPunchLists(listsRes.data || []);
      setContractors(contractorsRes.data || []);
      
      // Auto-expand first list if exists
      if (listsRes.data?.length > 0) {
        setExpandedLists({ [listsRes.data[0].id]: true });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load punch lists');
    } finally {
      setLoading(false);
    }
  };
  
  const createPunchList = async () => {
    if (!newList.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/projects/${projectId}/punch-lists`, newList);
      setPunchLists([res.data, ...punchLists]);
      setShowNewListDialog(false);
      setNewList({ title: '', description: '', phase: '' });
      setExpandedLists({ ...expandedLists, [res.data.id]: true });
      toast.success('Punch list created');
    } catch (error) {
      toast.error('Failed to create punch list');
    }
  };
  
  const addItem = async () => {
    if (!newItem.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    if (!selectedList) return;
    
    try {
      const res = await axios.post(`${API}/punch-lists/${selectedList.id}/items`, newItem);
      
      // Update local state
      setPunchLists(punchLists.map(pl => {
        if (pl.id === selectedList.id) {
          return {
            ...pl,
            items: [...(pl.items || []), res.data],
            stats: {
              ...pl.stats,
              total: (pl.stats?.total || 0) + 1,
              open: (pl.stats?.open || 0) + 1
            }
          };
        }
        return pl;
      }));
      
      setShowAddItemDialog(false);
      setNewItem({ description: '', location: '', category: '', priority: 'normal', assigned_to: '', notes: '' });
      toast.success('Item added');
    } catch (error) {
      // Wave 5.9 — surface the server's reason, never a bare "Failed" toast.
      const detail = error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to add item';
      toast.error(typeof detail === 'string' ? detail : 'Failed to add item');
    }
  };
  
  const toggleItemComplete = async (listId, item) => {
    const newStatus = item.status === 'completed' ? 'open' : 'completed';
    
    try {
      if (newStatus === 'completed') {
        await axios.post(`${API}/punch-lists/${listId}/items/${item.id}/complete`, null, {
          params: { completed_by_name: 'Admin' }
        });
      } else {
        await axios.put(`${API}/punch-lists/${listId}/items/${item.id}`, {
          status: 'open',
          completed_at: null,
          completed_by: null,
          completed_by_name: null
        });
      }
      
      // Update local state
      setPunchLists(punchLists.map(pl => {
        if (pl.id === listId) {
          return {
            ...pl,
            items: pl.items.map(i => {
              if (i.id === item.id) {
                return {
                  ...i,
                  status: newStatus,
                  completed_at: newStatus === 'completed' ? new Date().toISOString() : null
                };
              }
              return i;
            }),
            stats: {
              ...pl.stats,
              completed: newStatus === 'completed' ? (pl.stats?.completed || 0) + 1 : (pl.stats?.completed || 0) - 1,
              open: newStatus === 'completed' ? (pl.stats?.open || 0) - 1 : (pl.stats?.open || 0) + 1
            }
          };
        }
        return pl;
      }));
    } catch (error) {
      toast.error('Failed to update item');
    }
  };
  
  const deleteItem = async (listId, itemId) => {
    try {
      await axios.delete(`${API}/punch-lists/${listId}/items/${itemId}`);
      
      setPunchLists(punchLists.map(pl => {
        if (pl.id === listId) {
          const deletedItem = pl.items.find(i => i.id === itemId);
          return {
            ...pl,
            items: pl.items.filter(i => i.id !== itemId),
            stats: {
              ...pl.stats,
              total: (pl.stats?.total || 1) - 1,
              [deletedItem?.status || 'open']: (pl.stats?.[deletedItem?.status || 'open'] || 1) - 1
            }
          };
        }
        return pl;
      }));
      
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };
  
  const shareWithContractor = async () => {
    if (!shareContractorId || !selectedList) return;
    
    try {
      const res = await axios.post(
        `${API}/punch-lists/${selectedList.id}/share`,
        null,
        { params: { contractor_id: shareContractorId } }
      );
      
      // Copy share link
      const shareUrl = `${window.location.origin}/site-office/punch-list/${res.data.access_token}`;
      navigator.clipboard.writeText(shareUrl);
      
      setShowShareDialog(false);
      setShareContractorId('');
      toast.success(`Shared with ${res.data.contractor_name}. Link copied!`);
      
      // Refresh to update shared_with list
      fetchData();
    } catch (error) {
      toast.error('Failed to share punch list');
    }
  };

  // Wave 5.5 (2026-02) — "Notify trade" action.
  //
  // Sends BOTH a Resend email (with share link) AND an in-portal
  // message to every contractor with an assignment on the report.
  // Under the hood it:
  //   1. Creates a curated punch report (`punch-list/curated-report`)
  //      including every item in the list — audience `contractor`,
  //      photos + history included, 30-day expiry.
  //   2. Calls the new `notify-trade` endpoint against that share.
  const [notifying, setNotifying] = useState(false);
  const notifyTrade = async () => {
    if (!selectedList) return;
    if (!selectedList.project_id) {
      toast.error('This punch list has no project link — cannot notify.');
      return;
    }
    setNotifying(true);
    try {
      // Step 1 — create a curated share of every item.
      const items = selectedList.items || [];
      if (items.length === 0) {
        toast.error('Nothing to notify about — this list has no items.');
        setNotifying(false);
        return;
      }
      const shareRes = await axios.post(
        `${API}/projects/${selectedList.project_id}/punch-list-reports`,
        {
          item_ids: items.map((it) => it.id),
          audience: 'contractor',
          include_photos: true,
          include_history: true,
          expires_in_days: 30,
          title: `Punch List — ${selectedList.title || selectedList.name || ''}`,
        },
      );
      const shareId = shareRes.data?.share?.id;
      if (!shareId) {
        toast.error('Could not create shareable report.');
        setNotifying(false);
        return;
      }
      // Step 2 — dispatch email + portal message.
      const notifyRes = await axios.post(
        `${API}/projects/${selectedList.project_id}/punch-list-reports/${shareId}/notify-trade`,
        {},
      );
      const emails = notifyRes.data?.email_sent_count || 0;
      const portals = notifyRes.data?.portal_sent_count || 0;
      const emailTotal = notifyRes.data?.email_total || 0;
      const portalTotal = notifyRes.data?.portal_total || 0;
      if (emails === 0 && portals === 0) {
        toast.warning('Report created, but no contractors were reachable. Assign a contractor to items first.');
      } else {
        toast.success(`Notified: ${emails}/${emailTotal} email · ${portals}/${portalTotal} portal message${portalTotal === 1 ? '' : 's'}.`);
      }
      setShowShareDialog(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Notify trade failed.';
      toast.error(detail);
    } finally {
      setNotifying(false);
    }
  };
  
  const formatDuration = (createdAt, completedAt) => {
    if (!createdAt || !completedAt) return null;
    
    try {
      const created = new Date(createdAt);
      const completed = new Date(completedAt);
      const hours = Math.round((completed - created) / (1000 * 60 * 60) * 10) / 10;
      
      if (hours < 1) return `${Math.round(hours * 60)} min`;
      if (hours < 24) return `${hours} hrs`;
      return `${Math.round(hours / 24)} days`;
    } catch {
      return null;
    }
  };
  
  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  // Sort items: open first, then in_progress, then completed (completed at bottom)
  const sortItems = (items) => {
    const statusOrder = { open: 0, in_progress: 1, completed: 2 };
    return [...items].sort((a, b) => {
      const statusDiff = statusOrder[a.status || 'open'] - statusOrder[b.status || 'open'];
      if (statusDiff !== 0) return statusDiff;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#FAF9F7]'}`}>
        <RefreshCw className="w-8 h-8 animate-spin text-[#BC5D41]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D] text-[#E5E2DD]' : 'bg-[#FAF9F7]'}`} data-testid="quality-control-page">
      {/* Header */}
      <div className={`border-b ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'} sticky top-0 z-10`}>
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(`/projects/${projectId}/hub`)}
                className={darkMode ? 'text-[#8A8A8A] hover:text-[#E5E2DD]' : ''}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Project
              </Button>
              <div>
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#BC5D41]" />
                  Punch Lists
                  <HelpIcon elementId="quality-control-page" position="inline" />
                </h1>
                <p className="text-sm text-muted-foreground">{project?.name} - Punch Lists</p>
              </div>
            </div>
            
            <Button onClick={() => setShowNewListDialog(true)} data-testid="new-punch-list-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Punch List
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Punch Lists */}
        {punchLists.length === 0 ? (
          <div className={`text-center py-16 rounded-lg border-2 border-dashed ${darkMode ? 'border-[#1A1A1A]' : 'border-gray-200'}`}>
            <ListChecks className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No Punch Lists Yet</h3>
            <p className="text-muted-foreground mb-4">Create your first punch list to track items for completion.</p>
            <Button onClick={() => setShowNewListDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Punch List
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {punchLists.map(list => (
              <Collapsible
                key={list.id}
                open={expandedLists[list.id]}
                onOpenChange={(open) => setExpandedLists({ ...expandedLists, [list.id]: open })}
              >
                <div className={`rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0A0A0A]' : 'border-[#E5E2DD] bg-white'}`}>
                  {/* List Header */}
                  <CollapsibleTrigger asChild>
                    <div className={`p-4 cursor-pointer hover:bg-muted/30 flex items-center justify-between ${darkMode ? 'hover:bg-[#1A1A1A]' : ''}`}>
                      <div className="flex items-center gap-3">
                        {expandedLists[list.id] ? (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        )}
                        <div>
                          <h3 className="font-medium flex items-center gap-2">
                            {list.title}
                            {list.phase && (
                              <Badge variant="outline" className="text-xs">{list.phase}</Badge>
                            )}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {list.stats?.total || 0} items • {list.stats?.completed || 0} completed
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Progress */}
                        <div className="w-32 hidden sm:block">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-muted-foreground">Progress</span>
                            <span className="text-green-600">{list.stats?.completion_percent || 0}%</span>
                          </div>
                          <Progress value={list.stats?.completion_percent || 0} className="h-2" />
                        </div>
                        
                        {/* Actions */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              setSelectedList(list);
                              setShowAddItemDialog(true);
                            }}>
                              <Plus className="w-4 h-4 mr-2" />
                              Add Item
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              setSelectedList(list);
                              setShowShareDialog(true);
                            }}>
                              <Share2 className="w-4 h-4 mr-2" />
                              Share with Contractor
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete List
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  {/* List Items */}
                  <CollapsibleContent>
                    <div className={`border-t ${darkMode ? 'border-[#1A1A1A]' : 'border-[#E5E2DD]'}`}>
                      {/* Add Item Button */}
                      <div className={`px-4 py-2 border-b ${darkMode ? 'border-[#1A1A1A]' : 'border-[#E5E2DD]'}`}>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedList(list);
                            setShowAddItemDialog(true);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Item
                        </Button>
                        
                        {list.shared_with?.length > 0 && (
                          <span className="ml-4 text-xs text-muted-foreground">
                            Shared with: {list.shared_with.map(s => s.contractor_name).join(', ')}
                          </span>
                        )}
                      </div>
                      
                      {/* Items List */}
                      {(!list.items || list.items.length === 0) ? (
                        <div className="p-8 text-center text-muted-foreground">
                          <Square className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p>No items yet. Add your first punch list item.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-border">
                          {sortItems(list.items).map(item => (
                            <div 
                              key={item.id}
                              className={`px-4 py-3 flex items-start gap-3 transition-all ${
                                item.status === 'completed' 
                                  ? 'opacity-50 bg-muted/20' 
                                  : 'hover:bg-muted/10'
                              }`}
                            >
                              {/* Checkbox */}
                              <button
                                className="mt-0.5"
                                onClick={() => toggleItemComplete(list.id, item)}
                                data-testid={`toggle-item-${item.id}`}
                              >
                                {item.status === 'completed' ? (
                                  <CheckSquare className="w-5 h-5 text-green-600" />
                                ) : (
                                  <Square className="w-5 h-5 text-muted-foreground hover:text-[#BC5D41]" />
                                )}
                              </button>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className={item.status === 'completed' ? 'line-through' : ''}>
                                    <p className="font-medium">{item.description}</p>
                                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                                      {item.location && (
                                        <span className="flex items-center gap-1">
                                          <MapPin className="w-3 h-3" />
                                          {item.location}
                                        </span>
                                      )}
                                      {item.category && (
                                        <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                                      )}
                                      {item.assigned_contractor_name && (
                                        <span className="flex items-center gap-1">
                                          <User className="w-3 h-3" />
                                          {item.assigned_contractor_name}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-2">
                                    <Badge className={PRIORITY_CONFIG[item.priority]?.color || 'bg-blue-500'}>
                                      {item.priority}
                                    </Badge>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => deleteItem(list.id, item.id)}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Timestamps */}
                                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Created: {formatDateTime(item.created_at)}
                                  </span>
                                  {item.completed_at && (
                                    <>
                                      <span className="flex items-center gap-1 text-green-600">
                                        <CheckCircle2 className="w-3 h-3" />
                                        Completed: {formatDateTime(item.completed_at)}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Timer className="w-3 h-3" />
                                        Duration: {formatDuration(item.created_at, item.completed_at)}
                                      </span>
                                      {item.completed_by_name && (
                                        <span>by {item.completed_by_name}</span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        )}
      </div>
      
      {/* New Punch List Dialog */}
      <Dialog open={showNewListDialog} onOpenChange={setShowNewListDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Punch List</DialogTitle>
            <DialogDescription>Create a new punch list for tracking items.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input 
                placeholder="e.g., Pre-Drywall Inspection"
                value={newList.title}
                onChange={(e) => setNewList({ ...newList, title: e.target.value })}
                data-testid="punch-list-title-input"
              />
            </div>
            
            <div>
              <Label>Phase</Label>
              <Select value={newList.phase} onValueChange={(v) => setNewList({ ...newList, phase: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select phase..." />
                </SelectTrigger>
                <SelectContent>
                  {PHASE_OPTIONS.map(phase => (
                    <SelectItem key={phase} value={phase}>{phase}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea 
                placeholder="Optional description..."
                value={newList.description}
                onChange={(e) => setNewList({ ...newList, description: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewListDialog(false)}>Cancel</Button>
            <Button onClick={createPunchList} data-testid="create-punch-list-btn">Create Punch List</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Punch List Item</DialogTitle>
            <DialogDescription>Add an item to: {selectedList?.title}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Description *</Label>
              <Textarea 
                placeholder="What needs to be done?"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                data-testid="punch-item-description"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Location</Label>
                <Input 
                  placeholder="e.g., Master Bedroom"
                  value={newItem.location}
                  onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Category</Label>
                <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select value={newItem.priority} onValueChange={(v) => setNewItem({ ...newItem, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Assign to Contractor</Label>
                <Select value={newItem.assigned_to || "unassigned"} onValueChange={(v) => setNewItem({ ...newItem, assigned_to: v === "unassigned" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea 
                placeholder="Additional notes..."
                value={newItem.notes}
                onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>Cancel</Button>
            <Button onClick={addItem} data-testid="add-punch-item-btn">Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Punch List</DialogTitle>
            <DialogDescription>
              Share "{selectedList?.title}" with a contractor. They'll be able to view and mark items as complete.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Select Contractor</Label>
            <Select value={shareContractorId} onValueChange={setShareContractorId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose contractor..." />
              </SelectTrigger>
              <SelectContent>
                {contractors.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedList?.shared_with?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-2">Already shared with:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedList.shared_with.map(s => (
                    <Badge key={s.contractor_id} variant="secondary">
                      {s.contractor_name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>Cancel</Button>
            <Button
              variant="secondary"
              onClick={notifyTrade}
              disabled={notifying}
              data-testid="punch-notify-trade-btn"
              className="gap-1"
            >
              {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Notify trade (email + portal)
            </Button>
            <Button onClick={shareWithContractor} disabled={!shareContractorId}>
              <Share2 className="w-4 h-4 mr-2" />
              Share & Copy Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
